import { inject, Injectable } from "@angular/core";
import { secure } from "@calaosoft/osapp-common/rxjs/operators/secure";
import { IStoreDocument } from "@calaosoft/osapp-common/store/models/istore-document";
import { firstValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { DestroyableServiceBase } from "../../../services/models/destroyable-service-base";
import { SettingsService } from "../../../settings/services/settings.service";
import { ILogisticsSettings } from "../models/ilogistics-settings";
import { LogisticsSettings } from "../models/logistics-settings";

interface IUserSettings extends IStoreDocument {
	logistics?: ILogisticsSettings;
}

@Injectable()
export class LogisticsSettingsService extends DestroyableServiceBase {
	//#region FIELDS

	private readonly isvcSettings: SettingsService<IUserSettings> = inject(SettingsService);
	private readonly moLogisticsSettings$: Observable<LogisticsSettings> = this.initLogisticsSettings$().pipe(
		secure(this)
	);

	//#endregion FIELDS

	//#region METHODS

	constructor() {
		super();
	}

	private initLogisticsSettings$(): Observable<LogisticsSettings> {
		return this.isvcSettings
			.get$()
			.pipe(map((poUserSettings: IUserSettings) => new LogisticsSettings(poUserSettings.logistics)));
	}

	/** Retourne les paramètres logistiques. */
	public get$(): Observable<LogisticsSettings> {
		return this.moLogisticsSettings$;
	}

	/** Met à jour les paramètres logistiques.
	 * @param poLogisticsSetting Document des préférences utilisateur.
	 */
	public async updateAsync(poLogisticsSetting: LogisticsSettings): Promise<void> {
		const loUserSettings: IUserSettings = await this.isvcSettings.getAsync();

		loUserSettings.logistics = poLogisticsSetting;

		return this.isvcSettings.updateAsync(loUserSettings);
	}

	/** Récupère le document des préférences de l'utilisateur connecté. */
	public getAsync(): Promise<LogisticsSettings> {
		return firstValueFrom(this.get$());
	}

	/** Met à jour partiellement les paramètres logistiques (seulement ceux en paramètres sont traités).
	 * @param poPartialSettings Paramètres du module `Logistics` qu'on veut mettre à jour.
	 */
	public async updatePartialAsync(poPartialSettings: Partial<ILogisticsSettings>): Promise<void> {
		const loUserSettings: IUserSettings = await this.isvcSettings.getAsync();

		if (!loUserSettings.logistics) loUserSettings.logistics = {};

		Object.assign(loUserSettings.logistics, poPartialSettings);

		return this.isvcSettings.updateAsync(loUserSettings);
	}

	/** `true` si la page de tutoriel des livraisons est désactivée, `false` sinon. */
	public isDeliveriesTutorialDisabledAsync(): Promise<boolean> {
		return this.getAsync().then((poSettings: LogisticsSettings) => poSettings.deliveries?.disableTutorial === true);
	}

	//#endregion
}
